import React, { useEffect, useState } from 'react'
import { axiosGet, axiosPatch } from '../../../util/api'
import { Layout } from '../../../components/Layout'
import { Link, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { Dialog } from '../../../components/ui/dialog'
import { Button } from '../../../components/ui/button'
import { Toasts } from '../../../components/toaster/Toast'
import { SubscriptionStatus } from '../../../types/billing'
import { Callout } from '../../../components/ui/callout'
import { FormGroup, TextArea } from '@blueprintjs/core'
import User = Models.User
import Subscription = Models.Subscription
import { ProfilesTable } from './ProfilesTable'

const planLabels: Record<number, string> = {
    1: 'Beta',
    2: 'Launch',
    3: 'Launch v2',
    4: 'Standard v1',
    5: 'Sales Essentials',
    6: 'Sales Standard',
    7: 'Sales Premium',
    8: 'Spend Essentials',
    9: 'Spend Standard',
    10: 'Spend Premium',
}

const subscriptionStatus = (status: SubscriptionStatus) =>
    ({
        [SubscriptionStatus.Incomplete]: 'Incomplete',
        [SubscriptionStatus.IncompleteExpired]: 'Incomplete expired',
        [SubscriptionStatus.Trialing]: 'Trialing',
        [SubscriptionStatus.Active]: 'Active',
        [SubscriptionStatus.PastDue]: 'Past due',
        [SubscriptionStatus.Canceled]: 'Canceled',
        [SubscriptionStatus.Unpaid]: 'Unpaid',
    })[status] ?? 'Unknown'

const subscriptionPlan = (planType: number | null) =>
    planType == null ? 'Unassigned' : (planLabels[planType] ?? `Plan ${planType}`)

const subscriptionInterval = (interval?: number | null) => {
    if (interval === 1) return 'Monthly'
    if (interval === 2) return 'Yearly'
    return '—'
}

export const SubscriptionOverageDialog = ({
    user,
    subscription,
    close,
    onSaved,
}: {
    user: User
    subscription: Subscription
    close: () => void
    onSaved: () => void
}) => {
    const [reason, setReason] = useState('')
    const [saving, setSaving] = useState(false)
    const nextValue = !subscription.overage_billing_exempt

    const save = () => {
        if (!reason.trim()) return

        setSaving(true)
        axiosPatch(
            `admin/users/${user.id}/subscriptions/${subscription.id}/overage-billing-exemption`,
            {
                overage_billing_exempt: nextValue,
                reason: reason.trim(),
            },
        )
            .then(() => {
                Toasts.success(
                    nextValue ? 'Overage exemption enabled' : 'Overage exemption removed',
                )
                onSaved()
                close()
            })
            .catch(() => {
                setSaving(false)
            })
    }

    return (
        <Dialog
            isOpen
            onClose={saving ? undefined : close}
            title={nextValue ? 'Enable overage exemption' : 'Remove overage exemption'}
            description={
                nextValue
                    ? 'Skip future automatic overage charges for this subscription.'
                    : 'Resume future automatic overage charges for this subscription.'
            }
            className="bp4-card-rounded"
            canEscapeKeyClose={!saving}
            canOutsideClickClose={!saving}
        >
            <form
                onSubmit={(event) => {
                    event.preventDefault()
                    save()
                }}
            >
                <div className="p-4">
                    <p className="mt-0 mb-4 text-sm text-gray-700">
                        {nextValue
                            ? 'Skip future automatic overage charges for this subscription.'
                            : 'Resume future automatic overage charges for this subscription.'}
                    </p>
                    <div className="mb-4 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                        <div>
                            Subscription{' '}
                            <span className="font-medium text-gray-900">#{subscription.id}</span>
                        </div>
                        <div className="mt-1 text-xs">
                            Stripe ID: {subscription.subscription_id ?? 'Dummy subscription'}
                        </div>
                    </div>
                    <Callout intent="warning" className="mb-4">
                        This only affects future automatic overage charges. It does not void,
                        refund, or cancel existing Stripe invoice items.
                    </Callout>
                    <FormGroup
                        label="Reason"
                        labelFor="overage-exemption-reason"
                        labelInfo="(required)"
                        className="mb-0"
                    >
                        <TextArea
                            id="overage-exemption-reason"
                            fill
                            growVertically
                            autoFocus
                            disabled={saving}
                            value={reason}
                            placeholder="Why is this subscription being changed?"
                            onChange={(event) => setReason(event.target.value)}
                        />
                    </FormGroup>
                </div>
                <div className="flex justify-end gap-2 border-t p-4">
                    <Button type="button" disabled={saving} text="Cancel" onClick={close} />
                    <Button
                        type="submit"
                        loading={saving}
                        disabled={!reason.trim()}
                        intent={nextValue ? 'warning' : 'primary'}
                        text={nextValue ? 'Enable exemption' : 'Remove exemption'}
                    />
                </div>
            </form>
        </Dialog>
    )
}

export const SubscriptionsTable = ({ user, reload }: { user: User; reload: () => void }) => {
    const [selected, setSelected] = useState<Subscription | null>(null)

    return (
        <div className="bg-white p-3 border rounded-md mb-8 overflow-x-auto">
            <h2 className="text-lg font-bold mb-3">Subscriptions</h2>
            {(user.subscriptions?.length ?? 0) === 0 ? (
                <p className="text-gray-600">No subscriptions found.</p>
            ) : (
                <table className="w-full text-sm">
                    <thead className="text-left border-b">
                        <tr>
                            <th className="p-2">Subscription</th>
                            <th className="p-2">Plan</th>
                            <th className="p-2">Status</th>
                            <th className="p-2">Overages</th>
                            <th className="p-2">Latest override</th>
                            <th className="p-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {user.subscriptions?.map((subscription) => {
                            const change = subscription.latest_overage_exemption_change
                            const isCurrent = [
                                SubscriptionStatus.Active,
                                SubscriptionStatus.Trialing,
                                SubscriptionStatus.PastDue,
                            ].includes(subscription.status)
                            return (
                                <tr key={subscription.id} className="border-b last:border-0">
                                    <td className="p-2">
                                        <div>#{subscription.id}</div>
                                        <div className="text-xs text-gray-600">
                                            {subscription.subscription_id ?? 'Dummy subscription'}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            Created{' '}
                                            {format(new Date(subscription.created_at), 'M/d/yy')}
                                        </div>
                                    </td>
                                    <td className="p-2">
                                        <div>{subscriptionPlan(subscription.plan_type)}</div>
                                        <div className="text-xs text-gray-600">
                                            {subscriptionInterval(subscription.interval)}
                                        </div>
                                    </td>
                                    <td className="p-2">
                                        <div>{subscriptionStatus(subscription.status)}</div>
                                        {isCurrent && (
                                            <div className="text-xs text-blue-700">
                                                Current subscription
                                            </div>
                                        )}
                                        {subscription.cancel_at_period_end && (
                                            <div className="text-xs text-orange-700">
                                                Cancels at period end
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-2">
                                        <span
                                            className={
                                                subscription.overage_billing_exempt
                                                    ? 'text-amber-800 bg-amber-100 px-2 py-1 rounded'
                                                    : 'text-green-800 bg-green-100 px-2 py-1 rounded'
                                            }
                                        >
                                            {subscription.overage_billing_exempt
                                                ? 'Exempt'
                                                : 'Bill normally'}
                                        </span>
                                    </td>
                                    <td className="p-2 text-xs text-gray-600">
                                        {change ? (
                                            <>
                                                {change.changed_by?.email ?? 'Unknown admin'} ·{' '}
                                                {format(new Date(change.created_at), 'M/d/yy H:mm')}
                                                <br />
                                                {change.reason}
                                            </>
                                        ) : (
                                            'No override history'
                                        )}
                                    </td>
                                    <td className="p-2 text-right">
                                        <Button
                                            small
                                            text="Manage overages"
                                            onClick={() => setSelected(subscription)}
                                        />
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            )}
            {selected && (
                <SubscriptionOverageDialog
                    user={user}
                    subscription={selected}
                    close={() => setSelected(null)}
                    onSaved={reload}
                />
            )}
        </div>
    )
}

export const AdminUserDetail = () => {
    const { user_id } = useParams()
    const [user, setUser] = useState<User | null>(null)

    const loadUser = () => {
        axiosGet(`admin/users/${user_id}`)
            .then((r) => {
                setUser(r.data)
            })
            .catch(() => {})
    }

    useEffect(() => {
        loadUser()
    }, [user_id])

    if (!user) {
        return null
    }

    return (
        <Layout
            primary={
                <span>
                    <Link to="/admin/users" className="text-lg font-bold">
                        Users
                    </Link>
                    <b className="text-lg">&nbsp;/ {user_id}</b>
                </span>
            }
        >
            <div className="bg-white p-3 border rounded-md mb-8">
                <div>User Id: {user.id}</div>
                <div>Name: {user.name}</div>
                <div>Email: {user.email}</div>
            </div>
            <SubscriptionsTable user={user} reload={loadUser} />
            <ProfilesTable profiles={user.keys?.profiles ?? []} />
        </Layout>
    )
}
